import {
  Box,
  Typography,
  Switch,
  TextField,
  Button,
  Chip,
  Stack,
  IconButton,
  Divider,
  MenuItem,
  Paper,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import SecurityIcon from "@mui/icons-material/GppMaybe";
import LabelIcon from "@mui/icons-material/Label";
import DevicesIcon from "@mui/icons-material/Devices";
import SettingsIcon from "@mui/icons-material/Settings";

import { useEffect, useState } from "react";

import API from "../../../api";

import {
  startToast,
  updateToastError,
  updateToastSuccess,
} from "../../../utils/notification-loading";

import Notifications from "./Notification";

// ── Constants ─────────────────────────────────────────────────────────────────

const SECURITY_LEVELS = ["low", "medium", "high", "critical"];

const SECTIONS = [
  {
    id: "ot_security",
    label: "OT Security",
    description: "Security event alerts",
    icon: SecurityIcon,
  },
  {
    id: "ot_tags",
    label: "OT Tags",
    description: "Tag-based alerts",
    icon: LabelIcon,
  },
  {
    id: "new_hosts",
    label: "New Hosts",
    description: "New MAC address alerts",
    icon: DevicesIcon,
  },
  {
    id: "settings",
    label: "Settings",
    description: "General notification settings",
    icon: SettingsIcon,
  },
];

const DEFAULT_NOTIFICATIONS = {
  ot_security: {
    enabled: false,
    level: "low",
    hosts: [],
  },

  ot_tags: {
    enabled: false,
    hosts: [],
  },

  new_hosts: {
    enabled: false,
    filters: [],
  },
};

const inferEnabled = (section) => {
  if (!section) return false;

  if ("enabled" in section) {
    return !!section.enabled;
  }

  const hasHosts =
    Array.isArray(section.hosts) &&
    section.hosts.length > 0;

  const hasFilters =
    Array.isArray(section.filters) &&
    section.filters.length > 0;

  return hasHosts || hasFilters || "level" in section;
};

const buildNotifications = (raw) => ({
  ot_security: {
    ...DEFAULT_NOTIFICATIONS.ot_security,
    ...(raw?.ot_security ?? {}),
    enabled: inferEnabled(raw?.ot_security),
  },

  ot_tags: {
    ...DEFAULT_NOTIFICATIONS.ot_tags,
    ...(raw?.ot_tags ?? {}),
    enabled: inferEnabled(raw?.ot_tags),
  },

  new_hosts: {
    ...DEFAULT_NOTIFICATIONS.new_hosts,
    ...(raw?.new_hosts ?? {}),
    enabled: inferEnabled(raw?.new_hosts),
  },
});

// ── Shared styles ─────────────────────────────────────────────────────────────

const cardSx = {
  backgroundColor: "rgba(69, 144, 149, 0.05)",
  borderRadius: 2,
  border: "1px solid rgba(69, 144, 149, 0.1)",
  p: 3,
};

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#449095",
  },

  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#449095",
  },
};

// ── Validation ────────────────────────────────────────────────────────────────

const IPV4_RE =
  /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

const isValidIPv4 = (s) => {
  const m = s.match(IPV4_RE);

  return m
    ? m.slice(1).every((n) => Number(n) <= 255)
    : false;
};

const IPV6_RE =
  /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:(:[0-9a-fA-F]{1,4}){1,6}|:(:[0-9a-fA-F]{1,4}){1,7}|::)$/;

const isValidIPv6 = (s) => IPV6_RE.test(s);

const isValidIP = (s) =>
  isValidIPv4(s) || isValidIPv6(s);

const isValidCIDR = (s) => {
  const slash = s.lastIndexOf("/");

  if (slash === -1) {
    return false;
  }

  const addr = s.slice(0, slash);
  const prefix = Number(s.slice(slash + 1));

  if (
    !Number.isInteger(prefix) ||
    s.slice(slash + 1) === ""
  ) {
    return false;
  }

  if (isValidIPv4(addr)) {
    return prefix >= 0 && prefix <= 32;
  }

  if (isValidIPv6(addr)) {
    return prefix >= 0 && prefix <= 128;
  }

  return false;
};

const isValidEntry = (v) =>
  v === "ANY" || isValidIP(v) || isValidCIDR(v);

// ── SidebarItem ───────────────────────────────────────────────────────────────

const SidebarItem = ({
  section,
  selected,
  onClick,
}) => {
  const Icon = section.icon;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.25,
        cursor: "pointer",
        borderRadius: 1,

        backgroundColor: selected
          ? "rgba(69,144,149,0.14)"
          : "transparent",

        borderLeft: selected
          ? "3px solid #449095"
          : "3px solid transparent",

        transition: "all 0.15s",

        "&:hover": {
          backgroundColor: selected
            ? "rgba(69,144,149,0.14)"
            : "rgba(255,255,255,0.04)",
        },
      }}
    >
      <Icon
        sx={{
          fontSize: 20,
          color: selected ? "#449095" : "#666",
          flexShrink: 0,
        }}
      />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.85rem",
            fontWeight: selected ? 700 : 500,
            color: selected ? "#e8e8e8" : "#aaa",
            lineHeight: 1.3,
          }}
        >
          {section.label}
        </Typography>

        <Typography
          sx={{
            fontSize: "0.68rem",
            color: "#555",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {section.description}
        </Typography>
      </Box>
    </Box>
  );
};

// ── ListInput ─────────────────────────────────────────────────────────────────

const ListInput = ({
  items,
  onAdd,
  onRemove,
  placeholder = "e.g. 192.168.1.0/24",
}) => {
  const [val, setVal] = useState("");
  const [err, setErr] = useState("");

  const commit = (v = val.trim()) => {
    if (!v) return;

    if (!isValidEntry(v)) {
      setErr("Enter a valid IP, CIDR or ANY");
      return;
    }

    if ((items ?? []).includes(v)) {
      setErr("Already in list");
      return;
    }

    onAdd(v);

    setVal("");
    setErr("");
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        sx={{ mb: 1 }}
      >
        <TextField
          size="small"
          value={val}
          onChange={(e) => {
            setVal(e.target.value);
            setErr("");
          }}
          label="IP / CIDR / ANY"
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              commit();
            }
          }}
          error={!!err}
          helperText={
            err || "IPv4, IPv6, CIDR or ANY"
          }
          sx={{ flex: 1 }}
        />

        <Button
          size="small"
          variant="outlined"
          onClick={() => commit("ANY")}
          sx={{
            mt: 0.5,
            minWidth: 52,
            fontWeight: 700,
            fontSize: "0.72rem",
            color: "#7de8ed",
            borderColor: "rgba(125,232,237,0.35)",

            "&:hover": {
              borderColor: "#7de8ed",
              backgroundColor: "rgba(125,232,237,0.08)",
            },
          }}
        >
          ANY
        </Button>

        <IconButton
          onClick={() => commit()}
          sx={{
            color: "#449095",
            mt: 0.5,
          }}
        >
          <AddIcon />
        </IconButton>
      </Stack>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          minHeight: 28,
        }}
      >
        {(items ?? []).map((h, i) => (
          <Chip
            key={i}
            label={h}
            size="small"
            onDelete={() => onRemove(i)}
            sx={{
              fontFamily: "monospace",
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

// ── OT Security ───────────────────────────────────────────────────────────────

const OtSecuritySection = ({
  notif,
  setNotif,
}) => {
  const enabled = !!notif.ot_security?.enabled;

  const upd = (patch) =>
    setNotif((n) => ({
      ...n,
      ot_security: {
        ...n.ot_security,
        ...patch,
      },
    }));

  return (
    <Stack spacing={3}>
      <Box sx={cardSx}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{
            mb: enabled ? 2 : 0,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: enabled
                  ? "#449095"
                  : "rgba(69,144,149,0.4)",
                mb: 0.5,
              }}
            >
              OT Security Events
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.45)",
              }}
            >
              Receive email notifications when the OT analysis
              engine detects security events.
            </Typography>
          </Box>

          <Switch
            checked={enabled}
            onChange={(e) =>
              upd({
                enabled: e.target.checked,
              })
            }
            sx={switchSx}
          />
        </Stack>

        {enabled && (
          <>
            <Divider
              sx={{
                my: 2,
                borderColor: "rgba(255,255,255,0.07)",
              }}
            />

            <Stack spacing={2.5}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Minimum severity level
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.45)",
                    mb: 1.5,
                  }}
                >
                  Only send notifications for events at or above
                  the selected severity.
                </Typography>

                <TextField
                  select
                  size="small"
                  value={notif.ot_security?.level ?? "low"}
                  onChange={(e) =>
                    upd({
                      level: e.target.value,
                    })
                  }
                  sx={{
                    minWidth: 180,
                  }}
                >
                  {SECURITY_LEVELS.map((level) => (
                    <MenuItem
                      key={level}
                      value={level}
                    >
                      {level.charAt(0).toUpperCase() +
                        level.slice(1)}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Host filter
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.45)",
                    mb: 1.5,
                  }}
                >
                  Limit notifications to specific hosts. Click{" "}
                  <strong>ANY</strong> to receive alerts from all
                  hosts.
                </Typography>

                <ListInput
                  items={notif.ot_security?.hosts ?? []}
                  onAdd={(value) =>
                    upd({
                      hosts: [
                        ...(notif.ot_security?.hosts ?? []),
                        value,
                      ],
                    })
                  }
                  onRemove={(index) =>
                    upd({
                      hosts: (
                        notif.ot_security?.hosts ?? []
                      ).filter(
                        (_, currentIndex) =>
                          currentIndex !== index
                      ),
                    })
                  }
                />
              </Box>
            </Stack>
          </>
        )}
      </Box>
    </Stack>
  );
};

// ── OT Tags ───────────────────────────────────────────────────────────────────

const OtTagsSection = ({
  notif,
  setNotif,
}) => {
  const enabled = !!notif.ot_tags?.enabled;

  const upd = (patch) =>
    setNotif((n) => ({
      ...n,
      ot_tags: {
        ...n.ot_tags,
        ...patch,
      },
    }));

  return (
    <Stack spacing={3}>
      <Box sx={cardSx}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{
            mb: enabled ? 2 : 0,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: enabled
                  ? "#449095"
                  : "rgba(69,144,149,0.4)",
                mb: 0.5,
              }}
            >
              OT Tag Events
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.45)",
              }}
            >
              Receive email notifications for OT events based on
              tags associated with monitored assets.
            </Typography>
          </Box>

          <Switch
            checked={enabled}
            onChange={(e) =>
              upd({
                enabled: e.target.checked,
              })
            }
            sx={switchSx}
          />
        </Stack>

        {enabled && (
          <>
            <Divider
              sx={{
                my: 2,
                borderColor: "rgba(255,255,255,0.07)",
              }}
            />

            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Host filter
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.45)",
                  mb: 1.5,
                }}
              >
                Limit notifications to specific hosts or subnets.
                Click <strong>ANY</strong> for all hosts.
              </Typography>

              <ListInput
                items={notif.ot_tags?.hosts ?? []}
                onAdd={(value) =>
                  upd({
                    hosts: [
                      ...(notif.ot_tags?.hosts ?? []),
                      value,
                    ],
                  })
                }
                onRemove={(index) =>
                  upd({
                    hosts: (
                      notif.ot_tags?.hosts ?? []
                    ).filter(
                      (_, currentIndex) =>
                        currentIndex !== index
                    ),
                  })
                }
              />
            </Box>
          </>
        )}
      </Box>
    </Stack>
  );
};

// ── New Hosts ─────────────────────────────────────────────────────────────────

const NewHostsSection = ({
  notif,
  setNotif,
}) => {
  const enabled = !!notif.new_hosts?.enabled;

  const upd = (patch) =>
    setNotif((n) => ({
      ...n,
      new_hosts: {
        ...n.new_hosts,
        ...patch,
      },
    }));

  return (
    <Stack spacing={3}>
      <Box sx={cardSx}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{
            mb: enabled ? 2 : 0,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: enabled
                  ? "#449095"
                  : "rgba(69,144,149,0.4)",
                mb: 0.5,
              }}
            >
              New Host Discovery
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.45)",
              }}
            >
              Receive email notifications when a new MAC address
              is detected in the asset inventory.
            </Typography>
          </Box>

          <Switch
            checked={enabled}
            onChange={(e) =>
              upd({
                enabled: e.target.checked,
              })
            }
            sx={switchSx}
          />
        </Stack>

        {enabled && (
          <>
            <Divider
              sx={{
                my: 2,
                borderColor: "rgba(255,255,255,0.07)",
              }}
            />

            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Subnet filter
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.45)",
                  mb: 1.5,
                }}
              >
                Limit alerts to hosts seen in specific subnets.
                Click <strong>ANY</strong> to alert on all new
                hosts.
              </Typography>

              <ListInput
                items={notif.new_hosts?.filters ?? []}
                onAdd={(value) =>
                  upd({
                    filters: [
                      ...(notif.new_hosts?.filters ?? []),
                      value,
                    ],
                  })
                }
                onRemove={(index) =>
                  upd({
                    filters: (
                      notif.new_hosts?.filters ?? []
                    ).filter(
                      (_, currentIndex) =>
                        currentIndex !== index
                    ),
                  })
                }
                placeholder="e.g. 192.168.0.0/16"
              />
            </Box>
          </>
        )}
      </Box>
    </Stack>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const NotificationsSettings = ({
  notifications,
  setNotifications,
  anomalyNotify,
  offlineNotify,
  setOfflineNotify,
}) => {
  const [selectedId, setSelectedId] =
    useState("ot_security");

  const [notif, setNotif] = useState(() =>
    buildNotifications(notifications)
  );

  useEffect(() => {
    setNotif(
      buildNotifications(notifications)
    );
  }, [notifications]);

  const handleSave = () => {
    startToast();

    API.setNotifications({
      notifications: notif,
    })
      .then(() => {
        updateToastSuccess(
          "Notifications saved successfully"
        );

        if (setNotifications) {
          setNotifications(notif);
        }
      })
      .catch(() => {
        updateToastError(
          "Failed to save notifications"
        );
      });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        minHeight: 520,
        border: "1px solid rgba(69,144,149,0.15)",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "rgba(20,20,20,0.8)",
      }}
    >
      {/* SIDEBAR */}

      <Box
        sx={{
          width: 220,
          flexShrink: 0,
          borderRight: "1px solid #222",
          backgroundColor: "#111",
          py: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
        }}
      >
        <Typography
          sx={{
            px: 2,
            pb: 1,
            fontSize: "0.62rem",
            fontWeight: 700,
            color: "#555",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
          }}
        >
          Notifications
        </Typography>

        {SECTIONS.map((section) => (
          <SidebarItem
            key={section.id}
            section={section}
            selected={selectedId === section.id}
            onClick={() =>
              setSelectedId(section.id)
            }
          />
        ))}
      </Box>

      {/* CONTENT */}

      <Box
        sx={{
          flex: 1,
          p: 3.5,
          overflowY: "auto",
        }}
      >
        {selectedId === "ot_security" && (
          <OtSecuritySection
            notif={notif}
            setNotif={setNotif}
          />
        )}

        {selectedId === "ot_tags" && (
          <OtTagsSection
            notif={notif}
            setNotif={setNotif}
          />
        )}

        {selectedId === "new_hosts" && (
          <NewHostsSection
            notif={notif}
            setNotif={setNotif}
          />
        )}

        {selectedId === "settings" && (
          <Notifications
            anomalyNotify={anomalyNotify}
            offlineNotify={offlineNotify}
            setOfflineNotify={setOfflineNotify}
          />
        )}

        {selectedId !== "settings" && (
          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                backgroundColor: "#449095",
                color: "#fff",
                fontWeight: 700,
                px: 4,

                "&:hover": {
                  backgroundColor: "#3a7a7e",
                },
              }}
            >
              Save Notifications
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default NotificationsSettings;